import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Link,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArchive, FaChevronLeft, FaPen, FaUndo } from "react-icons/fa";
import {
  AccessScopeDto,
  DepartmentAdministrationDto,
  GrantRole,
  OrganizationRoleType,
  useActivateDepartmentMutation,
  useDeactivateDepartmentMutation,
  useListAccessGrantsQuery,
  useRenameDepartmentMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { GiveAccessForm } from "./GiveAccessForm";
import { PersonAccessModal } from "./PersonAccessModal";
import { administersRolesIn } from "./roleAdministration";
import { asOrganizationRole } from "./roleVocabulary";
import { SettingsCard } from "./SettingsCard";
import { useGrantRoleName } from "./useGrantRoleName";

interface Member {
  id: string;
  name: string;
  email: string;
  roles: string[];
  organizationRoles: OrganizationRoleType[];
}

const reachesEveryDepartment = (role: GrantRole) =>
  role === GrantRole.Owner || role === GrantRole.Admin;

interface Props {
  department: DepartmentAdministrationDto;
  onBack: () => void;
}

export const DepartmentAccess = ({ department, onBack }: Props) => {
  const authState = useLoggedInWithOrgContextState();
  const orgId = authState.selectedOrg.id;
  const managesDepartments = authState.selectedOrg.canManageUsers;
  const administersRoles = administersRolesIn(authState.selectedOrg, department.id);
  const { t } = useTranslation();
  const toast = useToast();
  const displayer = useApiError();
  const roleName = useGrantRoleName();
  const confirmationModal = useConfirmationModal();

  const { data, isLoading } = useListAccessGrantsQuery({
    orgId,
    departmentId: department.id,
  });
  const { data: organizationGrantData } = useListAccessGrantsQuery({ orgId });
  const [renameDepartment, { isLoading: isSavingName }] =
    useRenameDepartmentMutation();
  const [deactivateDepartment] = useDeactivateDepartmentMutation();
  const [activateDepartment, { isLoading: isReturningToUse }] =
    useActivateDepartmentMutation();

  const [isGivingAccess, setIsGivingAccess] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [showsOrganizationWide, setShowsOrganizationWide] = useState(false);
  const [draftName, setDraftName] = useState<string | null>(null);

  const isInUse =
    department.events > 0 ||
    department.contracts > 0 ||
    department.projects > 0 ||
    department.needs > 0;
  const canRetire = !department.isDefault && !isInUse;
  const retireHint = department.isDefault
    ? t("The default department cannot be retired")
    : t("The department still has sourcing events, contracts, projects or needs");

  const rename = async () => {
    const result = await renameDepartment({
      orgId,
      departmentId: department.id,
      departmentNameRequest: { name: draftName ?? "" },
    });
    if ("data" in result) {
      toast({ title: t("Department renamed!"), status: "success" });
      setDraftName(null);
    } else {
      displayer.trigger(result.error);
    }
  };

  const retire = async () => {
    const result = await deactivateDepartment({
      orgId,
      departmentId: department.id,
    });
    if ("data" in result) {
      toast({ title: t("Department retired!"), status: "success" });
      onBack();
    } else {
      displayer.trigger(result.error);
    }
  };

  const reactivate = async () => {
    const result = await activateDepartment({
      orgId,
      departmentId: department.id,
    });
    if ("data" in result) {
      toast({ title: t("Department is back in use!"), status: "success" });
    } else {
      displayer.trigger(result.error);
    }
  };

  const organizationGrants = useMemo(
    () =>
      (organizationGrantData?.grants ?? []).filter(
        (grant) => grant.scope === AccessScopeDto.Organization,
      ),
    [organizationGrantData],
  );

  const organizationRolesByPerson = useMemo(() => {
    const byPerson = new Map<string, OrganizationRoleType[]>();
    organizationGrants.forEach((grant) => {
      const role = asOrganizationRole(grant.role);
      if (role) {
        byPerson.set(grant.personId, [
          ...(byPerson.get(grant.personId) ?? []),
          role,
        ]);
      }
    });
    return byPerson;
  }, [organizationGrants]);

  const members: Member[] = useMemo(() => {
    const byPerson = new Map<string, Member>();
    (data?.grants ?? []).forEach((grant) => {
      const member = byPerson.get(grant.personId) ?? {
        id: grant.personId,
        name: `${grant.firstName} ${grant.lastName}`,
        email: grant.email,
        roles: [],
        organizationRoles: organizationRolesByPerson.get(grant.personId) ?? [],
      };
      member.roles.push(roleName(grant.role));
      byPerson.set(grant.personId, member);
    });
    return [...byPerson.values()];
  }, [data, organizationRolesByPerson, roleName]);

  const organizationWide = useMemo(() => {
    const byPerson = new Map<string, { id: string; name: string }>();
    organizationGrants
      .filter((grant) => reachesEveryDepartment(grant.role))
      .forEach((grant) =>
        byPerson.set(grant.personId, {
          id: grant.personId,
          name: `${grant.firstName ?? ""} ${grant.lastName ?? ""}`,
        }),
      );
    return [...byPerson.values()];
  }, [organizationGrants]);

  return (
    <>
      {confirmationModal.modal}

      <Button
        variant="link"
        size="sm"
        colorScheme="teal"
        leftIcon={<FaChevronLeft />}
        onClick={onBack}
        mt="5"
      >
        {t("Departments")}
      </Button>

      <Flex
        mt="3"
        justifyContent="space-between"
        alignItems="flex-start"
        gap="4"
        flexWrap="wrap"
      >
        <Box>
          {draftName === null ? (
            <Flex alignItems="baseline" columnGap="2">
              <Heading size="md">{department.name}</Heading>
              {department.isDefault && (
                <Text fontSize="xs" color="smMuted">
                  {t("default")}
                </Text>
              )}
              {!department.active && (
                <Badge colorScheme="orange">{t("Retired")}</Badge>
              )}
            </Flex>
          ) : (
            <Flex alignItems="center" columnGap="2">
              <Input
                size="sm"
                width="260px"
                backgroundColor="smBackground"
                value={draftName}
                autoFocus
                onChange={(e) => setDraftName(e.target.value)}
              />
              <Button
                size="sm"
                colorScheme="teal"
                isDisabled={
                  !draftName.trim() || draftName.trim() === department.name
                }
                isLoading={isSavingName}
                onClick={rename}
              >
                {t("Save")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setDraftName(null)}
              >
                {t("Cancel")}
              </Button>
            </Flex>
          )}

          <Text fontSize="sm" color="smMuted" mt="1">
            {t("{{count}} sourcing events", { count: department.events })} ·{" "}
            {t("{{count}} contracts", { count: department.contracts })} ·{" "}
            {t("{{count}} projects", { count: department.projects })} ·{" "}
            {t("{{count}} needs", { count: department.needs })}
          </Text>
        </Box>

        {managesDepartments && draftName === null && (
          <Flex columnGap="2">
            <Button
              size="sm"
              variant="ghost"
              colorScheme="teal"
              leftIcon={<FaPen />}
              onClick={() => setDraftName(department.name)}
            >
              {t("Rename")}
            </Button>
            {department.active ? (
              <Tooltip label={retireHint} isDisabled={canRetire}>
                {/* Chakra Tooltip never fires on a disabled button: it emits no pointer events. */}
                <Box display="inline-block">
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    leftIcon={<FaArchive />}
                    isDisabled={!canRetire}
                    onClick={() =>
                      confirmationModal.show({
                        title: t("Retire department?"),
                        description: t(
                          "It can no longer be chosen for new work, and the access people hold in it stops working. Nothing is deleted: returning it to use brings the access back.",
                        ),
                        onConfirmed: async () => {
                          await retire();
                        },
                      })
                    }
                  >
                    {t("Retire")}
                  </Button>
                </Box>
              </Tooltip>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FaUndo />}
                isLoading={isReturningToUse}
                onClick={reactivate}
              >
                {t("Return to use")}
              </Button>
            )}
          </Flex>
        )}
      </Flex>

      {isGivingAccess && (
        <GiveAccessForm
          departmentId={department.id}
          onDone={() => setIsGivingAccess(false)}
        />
      )}

      <SettingsCard
        title={t("Access")}
        actions={
          <Flex alignItems="center" gap="2">
            <Badge colorScheme={members.length ? "teal" : "gray"}>
              {t("{{count}} with access", { count: members.length })}
            </Badge>
            {administersRoles && !isGivingAccess && (
              <Button
                size="sm"
                colorScheme="teal"
                onClick={() => setIsGivingAccess(true)}
              >
                {t("Give access")}
              </Button>
            )}
          </Flex>
        }
        inset={false}
      >
        {members.length > 0 && (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th pl="5" borderBottomColor="smBorderSubtle">
                  {t("Name")}
                </Th>
                <Th borderBottomColor="smBorderSubtle">{t("Access here")}</Th>
                <Th pr="5" borderBottomColor="smBorderSubtle" />
              </Tr>
            </Thead>
            <Tbody>
              {members.map((member) => (
                <Tr
                  key={member.id}
                  _hover={{ backgroundColor: "smBackgroundSecondary" }}
                >
                  <Td pl="5" borderBottomColor="smBorderSubtle">
                    <Text fontSize="sm">{member.name}</Text>
                    <Text
                      fontSize="xs"
                      color="smMuted"
                      isTruncated
                      title={member.email}
                    >
                      {member.email}
                    </Text>
                  </Td>
                  <Td borderBottomColor="smBorderSubtle">
                    {member.roles.map((role) => (
                      <Text
                        key={role}
                        fontSize="sm"
                        fontWeight="semibold"
                        lineHeight="1.7"
                      >
                        {role}
                      </Text>
                    ))}
                  </Td>
                  <Td
                    pr="5"
                    borderBottomColor="smBorderSubtle"
                    textAlign="right"
                  >
                    {administersRoles && (
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="teal"
                        onClick={() => setEditing(member)}
                      >
                        {t("Change access")}
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}

        {!members.length && (
          <Box px="5" py="6">
            <Text fontSize="sm" color="smMuted">
              {isLoading
                ? `${t("Loading")}...`
                : t("Nobody has access in this department yet")}
            </Text>
          </Box>
        )}

        {organizationWide.length > 0 && (
          <Box
            px="5"
            py="3"
            backgroundColor="smBackgroundSecondary"
            borderTop="1px solid"
            borderColor="smBorder"
          >
            <Text fontSize="xs" color="smMuted">
              {t(
                "The owner and organization-wide administrators reach in here too.",
              )}{" "}
              <Link
                as="button"
                color="smPrimary"
                onClick={() => setShowsOrganizationWide((shown) => !shown)}
              >
                {showsOrganizationWide
                  ? t("Hide")
                  : t("Show the {{count}}", { count: organizationWide.length })}
              </Link>
            </Text>
            {showsOrganizationWide &&
              organizationWide.map((person) => (
                <Text key={person.id} fontSize="xs" color="smMuted" mt="1">
                  {person.name}
                </Text>
              ))}
          </Box>
        )}
      </SettingsCard>

      {editing && (
        <PersonAccessModal
          personId={editing.id}
          personName={editing.name}
          organizationRoles={editing.organizationRoles}
          isOpen
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
};
